import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import CustomTextField from "../../../../components/common/Textfield";
import { Venue } from "../../../../types/venue";

interface VenueDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: () => void;
  loading: boolean;
  newVenue: Venue;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  cities: string[];
  theme: any;
}

const VenueDialog: React.FC<VenueDialogProps> = ({
  open,
  onClose,
  onSubmit,
  loading,
  newVenue,
  onChange,
  cities,
  theme,
}) => {
  const textFieldStyles = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      "& fieldset": {
        borderColor: theme.primary_colors?.[300] || "#ddd",
      },
      "&:hover fieldset": {
        borderColor: theme.neutral_colors?.[400] || "#bbb",
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.primary_colors?.[400] || "#614438",
      },
    },
    "& .MuiInputLabel-root": {
      color: theme.primary_colors?.[300] || "#666",
    },
    "& .MuiInputBase-input": {
      color: theme.neutral_colors?.[100] || "#333",
    },
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          backgroundColor: theme.neutral_colors?.[900] || "#fff",
          color: theme.neutral_colors?.[100] || "#333",
        }}
      >
        Add New Venue
      </DialogTitle>

      <DialogContent
        sx={{ backgroundColor: theme.neutral_colors?.[900] || "#fff" }}
      >
        {[
          { label: "Venue Name", name: "name", required: true },
          { label: "Address One", name: "address_one", required: true },
          { label: "Address Two", name: "address_two", required: false },
          { label: "Suburb", name: "suburb", required: true },
        ].map(({ label, name, required }) => (
          <CustomTextField
            key={name}
            fullWidth
            label={label}
            name={name}
            value={(newVenue as any)[name]}
            onChange={onChange}
            variant="outlined"
            margin="normal"
            required={required}
            sx={textFieldStyles}
          />
        ))}

        <CustomTextField
          fullWidth
          label="City"
          name="city"
          value={newVenue.city}
          onChange={onChange}
          variant="outlined"
          margin="normal"
          select
          required
          sx={textFieldStyles}
        >
          <MenuItem value="">
            <em>None</em>
          </MenuItem>
          {cities.map((city) => (
            <MenuItem key={city} value={city}>
              {city}
            </MenuItem>
          ))}
        </CustomTextField>
      </DialogContent>

      <DialogActions
        sx={{ backgroundColor: theme.neutral_colors?.[900] || "#fff" }}
      >
        <Button
          onClick={onClose}
          sx={{
            color: theme.primary_colors?.[100] || "#614438",
            borderColor: theme.primary_colors?.[100] || "#ddd",
            backgroundColor: theme.primary_colors?.[800],
            "&:hover": {
              borderColor: theme.neutral_colors?.[400] || "#bbb",
              backgroundColor: theme.neutral_colors?.[800] || "#f9f9f9",
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={onSubmit}
          variant="contained"
          disabled={
            loading || !newVenue.name || !newVenue.address_one || !newVenue.city
          }
          sx={{
            backgroundColor: theme.primary_colors?.[400] || "#614438",
            color: theme.primary_colors?.[900] || "#fff",
            "&:hover": {
              backgroundColor: theme.primary_colors?.[500] || "#503629",
            },
            "&.Mui-disabled": {
              backgroundColor: theme.neutral_colors?.[400] || "#ddd",
              color: theme.primary_colors?.[900] || "#666",
            },
          }}
        >
          {loading ? (
            <CircularProgress
              size={20}
              sx={{ color: theme.neutral_colors?.[100] || "#fff" }}
            />
          ) : (
            "Add Venue"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VenueDialog;
